import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './MissingSourceDataErrorsTable.translations.json';
import {
  formatCodeName,
  formatDate,
  MissingSourceDataErrorType,
  Table,
} from '@atlas-frontend/sdk';
import { createColumnHelper } from '@tanstack/react-table';
import { FC, useMemo } from 'react';
import { useErrorReportDescriptionOptions } from './useErrorReportDescriptionOptions';
import { useDescriptionFilter } from './useDescriptionFilter';

const { addTranslations, t } = i18nNsFactory(
  'missing-source-data-errors-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<MissingSourceDataErrorType>();

export type MissingSourceDataErrorsTableProps = {
  data: MissingSourceDataErrorType[];
  scrollContainerSelector: string;
};

export const MissingSourceDataErrorsTable: FC<
  MissingSourceDataErrorsTableProps
> = ({ data, scrollContainerSelector }) => {
  const { filters, setFilters } = useDescriptionFilter({
    data,
    filterId: 'dataObjectDescription',
    getDescriptionId: (error) => error.dataObjectDescriptionId,
    getFilterValue: formatParentCodeName,
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor((rowData) => rowData.createdAt, {
        id: 'sequenceNumber',
        header: t('column.sequenceNumber'),
        cell: (props) => props.row.index + 1,
        enableColumnFilter: false,
        meta: {
          width: 100,
          className: 'sticky-left',
        },
      }),
      columnHelper.accessor(formatParentCodeName, {
        id: 'dataObjectDescription',
        header: t('column.dataObjectDescription'),
        filterFn: 'select',
      }),
      columnHelper.accessor(formatChildCodeName, {
        id: 'sourceDataObjectDescription',
        header: t('column.sourceDataObjectDescription'),
        filterFn: 'select',
      }),
      columnHelper.accessor((rowData) => formatDate(rowData.valueDate), {
        id: 'valueDate',
        header: t('column.valueDate'),
        filterFn: 'formattedDate',
      }),
    ],
    [],
  );

  const descriptionOptions = useErrorReportDescriptionOptions(data);

  const options = useMemo(
    () => ({
      dataObjectDescription: descriptionOptions,
      sourceDataObjectDescription: [
        ...new Set(data.map(formatChildCodeName)),
      ].map((codeName) => ({ id: codeName, name: codeName })),
    }),
    [descriptionOptions, data],
  );

  return (
    <Table
      columns={columns}
      data={data}
      options={options}
      filters={filters}
      onFilter={setFilters}
      name="missing-source-data-errors-table"
      sticky
      scrollContainerSelector={scrollContainerSelector}
    />
  );
};

const formatParentCodeName = (error: MissingSourceDataErrorType) =>
  formatCodeName({
    code: error.dataObjectDescriptionCode,
    name: error.dataObjectDescriptionName,
  });

const formatChildCodeName = (error: MissingSourceDataErrorType) =>
  formatCodeName({
    code: error.sourceDataObjectDescriptionCode,
    name: error.sourceDataObjectDescriptionName,
  });
